import { useState, useEffect } from 'react';
import { CheckCircle, XCircle, AlertTriangle, Loader2, Clock, Lock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { submitKnowledgeCheck, SubmitKnowledgeCheckOutputType } from 'zite-endpoints-sdk';
import { toast } from 'sonner';

type Question = {
  id: string; questionText: string;
  options: { label: string; value: string }[];
  isCritical: boolean; sortOrder: number;
};

const MAX_ATTEMPTS = 3;

export default function CheckStep({ questions, progressId, moduleId, enrollmentId, alreadyPassed, previousScore, attempts, onComplete }: {
  questions: Question[];
  progressId: string;
  moduleId: string;
  enrollmentId: string;
  alreadyPassed: boolean;
  previousScore: number;
  attempts: { attemptNumber: number; score: number; passed: boolean; submittedAt: string }[];
  onComplete: (passed: boolean, score: number) => void;
}) {
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<SubmitKnowledgeCheckOutputType | null>(null);
  const [lockedUntil, setLockedUntil] = useState<string | null>(null);
  const [lockCountdown, setLockCountdown] = useState('');

  // Check existing lockout from attempts
  useEffect(() => {
    const recentFails = attempts.filter(a => !a.passed).sort((a, b) =>
      new Date(b.submittedAt).getTime() - new Date(a.submittedAt).getTime()
    ).slice(0, MAX_ATTEMPTS);
    if (recentFails.length >= MAX_ATTEMPTS) {
      const oldest = recentFails[recentFails.length - 1];
      const lockEnd = new Date(oldest.submittedAt).getTime() + 15 * 60 * 1000;
      if (Date.now() < lockEnd) {
        setLockedUntil(new Date(lockEnd).toISOString());
      }
    }
  }, [attempts]);

  // Countdown timer
  useEffect(() => {
    if (!lockedUntil) { setLockCountdown(''); return; }
    const tick = () => {
      const remain = new Date(lockedUntil).getTime() - Date.now();
      if (remain <= 0) { setLockedUntil(null); setLockCountdown(''); return; }
      const mins = Math.floor(remain / 60000);
      const secs = Math.floor((remain % 60000) / 1000);
      setLockCountdown(`${mins}:${secs.toString().padStart(2, '0')}`);
    };
    tick();
    const iv = setInterval(tick, 1000);
    return () => clearInterval(iv);
  }, [lockedUntil]);

  if (alreadyPassed) {
    return (
      <div className="space-y-4">
        <div className="bg-primary/10 rounded-xl p-6 text-center">
          <CheckCircle className="w-12 h-12 text-primary mx-auto mb-3" />
          <h2 className="text-lg font-bold text-foreground">Knowledge Check Passed</h2>
          <p className="text-sm text-muted-foreground mt-1">Score: {previousScore}%</p>
        </div>
        <Button onClick={() => onComplete(true, previousScore)} className="w-full h-12 text-base">Continue</Button>
      </div>
    );
  }

  if (questions.length === 0) {
    return (
      <div className="space-y-4">
        <h2 className="text-lg font-bold text-foreground">Knowledge Check</h2>
        <p className="text-sm text-muted-foreground">No questions configured for this module yet.</p>
        <Button onClick={() => onComplete(true, 100)} className="w-full h-12 text-base">Continue</Button>
      </div>
    );
  }

  // Locked out
  if (lockedUntil && !result) {
    return (
      <div className="space-y-4">
        <div className="bg-destructive/10 rounded-xl p-6 text-center">
          <Lock className="w-12 h-12 text-destructive mx-auto mb-3" />
          <h2 className="text-lg font-bold text-foreground">Temporarily Locked</h2>
          <p className="text-sm text-muted-foreground mt-2">
            You've used all {MAX_ATTEMPTS} attempts. Please review the learning material before trying again.
          </p>
          {lockCountdown && (
            <div className="flex items-center justify-center gap-2 mt-3 text-lg font-mono font-bold text-destructive">
              <Clock className="w-5 h-5" /> {lockCountdown}
            </div>
          )}
        </div>
        <div className="text-xs text-muted-foreground text-center">
          Previous attempts: {attempts.length} · Best score: {Math.max(...attempts.map(a => a.score), 0)}%
        </div>
      </div>
    );
  }

  const handleSubmit = async () => {
    if (Object.keys(answers).length < questions.length) {
      toast.error('Please answer all questions before submitting');
      return;
    }
    setSubmitting(true);
    const answerArray = questions.map(q => ({ questionId: q.id, selectedValue: answers[q.id] || '' }));
    const res = await submitKnowledgeCheck({ progressId, moduleId, enrollmentId, answers: answerArray }).catch(e => {
      if (e?.message?.includes('attempts')) {
        // Server-enforced lockout
        toast.error(e.message);
        setLockedUntil(new Date(Date.now() + 15 * 60 * 1000).toISOString());
      } else {
        toast.error(e?.message || 'Failed to submit');
      }
      return null;
    });
    setSubmitting(false);
    if (res) {
      setResult(res);
      if (res.lockedUntil) setLockedUntil(res.lockedUntil);
      onComplete(res.passed, res.score);
    }
  };

  if (result) {
    return (
      <div className="space-y-4">
        <div className={`rounded-xl p-6 text-center ${result.passed ? 'bg-primary/10' : 'bg-destructive/10'}`}>
          {result.passed ? <CheckCircle className="w-12 h-12 text-primary mx-auto mb-3" /> : <XCircle className="w-12 h-12 text-destructive mx-auto mb-3" />}
          <h2 className="text-lg font-bold text-foreground">{result.passed ? 'Passed!' : 'Not Yet'}</h2>
          <p className="text-2xl font-bold mt-1">{result.score}%</p>
          <p className="text-sm text-muted-foreground">{result.correct} of {result.total} correct</p>
          {result.criticalFailed && (
            <div className="flex items-center justify-center gap-1 mt-2 text-destructive text-sm">
              <AlertTriangle className="w-4 h-4" /> Critical question failed
            </div>
          )}
        </div>

        <div className="space-y-3">
          {result.feedback.map((fb) => {
            const q = questions.find(qq => qq.id === fb.questionId);
            return (
              <div key={fb.questionId} className={`border rounded-xl p-3 ${fb.correct ? 'border-primary/30 bg-primary/5' : 'border-destructive/30 bg-destructive/5'}`}>
                <div className="flex items-start gap-2">
                  {fb.correct ? <CheckCircle className="w-4 h-4 text-primary mt-0.5 shrink-0" /> : <XCircle className="w-4 h-4 text-destructive mt-0.5 shrink-0" />}
                  <div>
                    <p className="text-sm font-medium text-foreground">{q?.questionText}</p>
                    {!fb.correct && <p className="text-xs text-muted-foreground mt-1">Correct: {q?.options.find(o => o.value === fb.correctAnswer)?.label}</p>}
                    {fb.explanation && <p className="text-xs text-foreground/70 mt-1">{fb.explanation}</p>}
                    {fb.isCritical && <span className="text-[10px] font-bold text-destructive uppercase mt-1 inline-block">Critical</span>}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {!result.passed && !lockedUntil && (
          <Button onClick={() => { setResult(null); setAnswers({}); }} variant="outline" className="w-full h-12 text-base">
            Retry Knowledge Check
          </Button>
        )}
        {!result.passed && lockedUntil && lockCountdown && (
          <div className="text-center text-sm text-muted-foreground flex items-center justify-center gap-2">
            <Clock className="w-4 h-4" /> Next attempt available in {lockCountdown}
          </div>
        )}
      </div>
    );
  }

  const attemptsUsed = attempts.filter(a => !a.passed).length;
  const attemptsRemaining = Math.max(0, MAX_ATTEMPTS - attemptsUsed);

  return (
    <div className="space-y-4">
      <h2 className="text-lg font-bold text-foreground">Knowledge Check</h2>
      <p className="text-sm text-muted-foreground">Answer all {questions.length} questions. You need 80% to pass. Critical questions must be correct.</p>

      {attempts.length > 0 && (
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span>Attempts used: {attemptsUsed}/{MAX_ATTEMPTS}</span>
          <span>Best: {Math.max(...attempts.map(a => a.score), 0)}%</span>
        </div>
      )}
      {attemptsRemaining <= 1 && attemptsRemaining > 0 && (
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 text-sm text-amber-600 flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 shrink-0" />
          <span>Last attempt before 15-minute lockout. Review your answers carefully.</span>
        </div>
      )}

      <div className="space-y-4">
        {questions.map((q, i) => (
          <div key={q.id} className="border border-border rounded-xl p-4">
            <div className="flex items-start gap-2 mb-3">
              <span className="text-xs font-bold text-muted-foreground">{i + 1}.</span>
              <div className="flex-1">
                <p className="text-sm font-medium text-foreground">{q.questionText}</p>
                {q.isCritical && <span className="text-[10px] font-bold text-destructive uppercase">Critical</span>}
              </div>
            </div>
            <div className="space-y-2 ml-4">
              {q.options.map(opt => (
                <button key={opt.value} onClick={() => setAnswers(prev => ({ ...prev, [q.id]: opt.value }))}
                  className={`w-full text-left rounded-lg p-3 text-sm transition-all border ${
                    answers[q.id] === opt.value ? 'border-primary bg-primary/10 text-foreground font-medium' : 'border-border bg-card text-foreground hover:bg-muted/50'
                  }`}>
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>

      <Button onClick={handleSubmit} disabled={submitting || Object.keys(answers).length < questions.length}
        className="w-full h-12 text-base">
        {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Submitting…</> : `Submit Answers (${Object.keys(answers).length}/${questions.length})`}
      </Button>
    </div>
  );
}
